import { formatJalaliLong, formatJalaliShort, isoToJalali } from '@airachat/solar';
import type { Currency, Metric } from '../types';

export function jalaliDate(iso?: string | null, style: 'short' | 'long' = 'short'): string {
  if (!iso) return 'بدون تاریخ';
  try {
    return style === 'long' ? formatJalaliLong(isoToJalali(iso.slice(0, 10))) : formatJalaliShort(isoToJalali(iso.slice(0, 10)));
  } catch {
    return 'تاریخ نامعتبر';
  }
}

export function faNumber(value: number, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat('fa-IR', { maximumFractionDigits }).format(value);
}

/**
 * Money is stored in the company's own reporting currency and displayed in
 * whichever unit the reader picked, so the conversion happens here once. A
 * missing figure is «ناموجود», never 0 — the two mean opposite things.
 */
export function formatMoney(value: number | null | undefined, source: Currency, display: Currency): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'ناموجود';
  const rial = source === 'toman' ? value * 10 : value;
  const shown = display === 'toman' ? rial / 10 : rial;
  return `${faNumber(shown, 0)} ${display === 'toman' ? 'تومان' : 'ریال'}`;
}

const SCALES: Array<[number, string]> = [
  [1_000_000_000_000, 'همت'],
  [1_000_000_000, 'میلیارد'],
  [1_000_000, 'میلیون'],
  [1_000, 'هزار'],
];

/**
 * A money figure short enough for a chart axis or a tile — «۲۴۵٫۷ میلیارد».
 *
 * Only ever used where the exact figure is also available in full somewhere on
 * the same screen. An abbreviated amount is a label, not a statement of record,
 * and nothing that gets filed is allowed to read from this.
 */
export function compactMoney(value: number | null | undefined, source: Currency, display: Currency): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'ناموجود';
  const rial = source === 'toman' ? value * 10 : value;
  const shown = display === 'toman' ? rial / 10 : rial;
  const sign = shown < 0 ? '−' : '';
  const size = Math.abs(shown);
  const scale = SCALES.find(([threshold]) => size >= threshold);
  if (!scale) return `${sign}${faNumber(size)}`;
  const [threshold, word] = scale;
  return `${sign}${faNumber(size / threshold, size / threshold < 10 ? 1 : 0)} ${word}`;
}

export function metricDisplay(metric: Metric, source: Currency, display: Currency): string {
  // «قابل محاسبه نیست» is a statement about the data, and it has to survive all
  // the way to the screen rather than being rendered as a zero.
  if (metric.value === null) return 'قابل محاسبه نیست';
  if (metric.unit === 'amount') return formatMoney(metric.value, source, display);
  if (metric.unit === 'percent') return `${faNumber(metric.value, 2)}٪`;
  if (metric.unit === 'days') return `${faNumber(metric.value, 1)} روز`;
  return faNumber(metric.value, 2);
}

/** Percentage change between two periods; null whenever the base cannot support one. */
export function changeRatio(current: number, previous: number | null): number | null {
  if (previous === null || previous === 0 || !Number.isFinite(previous)) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function statusLabel(status?: string): string {
  return ({
    queued: 'در صف', running: 'در حال پردازش', succeeded: 'تکمیل‌شده', failed: 'ناموفق',
    cancelled: 'لغوشده', awaiting_mapping: 'در انتظار تأیید شما',
    pending: 'در انتظار', skipped: 'رد شده',
    active: 'فعال', archived: 'بایگانی', suspended: 'غیرفعال',
    approved: 'تأییدشده', rejected: 'ردشده', review_required: 'نیازمند تأیید', draft: 'پیش‌نویس',
    suggested: 'پیشنهادی', edited: 'ویرایش‌شده',
    open: 'باز', resolved: 'رسیدگی‌شده', ignored: 'نادیده گرفته‌شده',
    closed: 'بسته', configured: 'فعال',
    engine: 'موتور محاسبات', ai: 'هوش مصنوعی', user: 'کاربر',
    executive: 'خلاصه مدیریتی', standard: 'استاندارد', full: 'کامل',
  } as Record<string, string>)[status ?? ''] ?? status ?? 'نامشخص';
}

export function severityLabel(severity: string): string {
  return ({
    critical: 'بحرانی', high: 'زیاد', medium: 'متوسط', low: 'کم', info: 'اطلاعی',
  } as Record<string, string>)[severity] ?? severity;
}

export function accessLabel(level?: string): string {
  return ({
    none: 'بدون دسترسی', read: 'مشاهده', write: 'ویرایش', admin: 'مدیریت کامل',
  } as Record<string, string>)[level ?? ''] ?? 'نامشخص';
}

export function statementLabel(kind: string): string {
  return ({
    // The titles prescribed by استاندارد حسابداری شماره ۱ (تجدیدنظرشده ۱۳۹۷),
    // in force since ۱۳۹۸: «ترازنامه» and «صورت جریان وجوه نقد» are the
    // superseded names. Kept in step with STATEMENT_TITLE_FA on the server —
    // the client cannot import it without pulling xlsx into the bundle.
    balance: 'صورت وضعیت مالی', income: 'صورت سود و زیان',
    cashflow: 'صورت جریان‌های نقدی', equity: 'صورت تغییرات در حقوق مالکانه',
  } as Record<string, string>)[kind] ?? kind;
}

export function sectionLabel(section: string): string {
  return ({
    current_assets: 'دارایی‌های جاری', non_current_assets: 'دارایی‌های غیرجاری',
    current_liabilities: 'بدهی‌های جاری', non_current_liabilities: 'بدهی‌های غیرجاری',
    equity: 'حقوق مالکانه', revenue: 'درآمدها', cogs: 'بهای تمام‌شده',
    operating_expense: 'هزینه‌های عملیاتی', other_income: 'سایر درآمدها',
    financial_expense: 'هزینه‌های مالی', other_expense: 'سایر هزینه‌ها',
    tax_expense: 'مالیات بر درآمد', summary: 'جمع‌بندی',
    operating: 'فعالیت‌های عملیاتی', investing: 'فعالیت‌های سرمایه‌گذاری',
    financing: 'فعالیت‌های تأمین مالی', unattributed: 'بدون سند قابل انتساب',
  } as Record<string, string>)[section] ?? section;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '۰';
  const units = ['بایت', 'کیلوبایت', 'مگابایت', 'گیگابایت'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${faNumber(bytes / 1024 ** index, index === 0 ? 0 : 1)} ${units[index]}`;
}

export function actionLabel(action: string): string {
  const known: Record<string, string> = {
    'auth.login': 'ورود به سامانه',
    'auth.logout': 'خروج از سامانه',
    'auth.login_failed': 'تلاش ناموفق برای ورود',
    'auth.login_throttled': 'ورود به دلیل تلاش‌های مکرر متوقف شد',
    'password.changed': 'تغییر رمز عبور',
    'user.created': 'ساخت کاربر',
    'user.updated': 'ویرایش کاربر',
    'user.password_changed': 'تغییر رمز عبور',
    'user.password_reset': 'بازنشانی رمز عبور کاربر',
    'company.created': 'ساخت شرکت',
    'company.updated': 'ویرایش شرکت',
    'company.archived': 'بایگانی شرکت',
    'company.access_changed': 'تغییر دسترسی شرکت',
    'fiscal_year.created': 'ساخت سال مالی',
    'fiscal_year.updated': 'تغییر وضعیت سال مالی',
    'run.started': 'شروع پردازش',
    'run.executed': 'ادامه پردازش',
    'account.reclassified': 'تغییر طبقه حساب',
    'mapping.decided': 'تصمیم درباره نگاشت ستون‌ها',
    'mapping.approved': 'تأیید نگاشت ستون‌ها',
    'mapping.rejected': 'رد نگاشت ستون‌ها',
    'quality.resolved': 'رسیدگی به مسئله کیفیت داده',
    'ai.asked': 'پرسش از تحلیل‌گر',
    'ai.integrity_checked': 'بررسی صحت اطلاعات',
    'report.generated': 'ساخت گزارش',
    'report.decided': 'تصمیم درباره گزارش',
    'report.approved': 'تأیید گزارش',
    'report.rejected': 'رد گزارش',
    'settings.updated': 'تغییر تنظیمات',
    'rule.saved': 'ثبت قاعده',
    'rule.deleted': 'حذف قاعده',
    'template.saved': 'ثبت قالب گزارش',
    'backup.created': 'ساخت نسخه پشتیبان',
    'backup.deleted': 'حذف نسخه پشتیبان',
    'ai.call': 'فراخوانی مدل زبانی',
  };
  return known[action] ?? action;
}
